/**
 * A character: what one is, and what the drawing needs to know to draw it.
 *
 * ## Ported, not reinvented
 *
 * The shapes, the palettes and the expression table all come from the
 * generator in `dev/`. The drawing itself is machine-converted from that
 * template - a hundred and sixty-nine shapes across eighty-six conditionals,
 * which is far too many to retype without quietly getting one path wrong. This
 * file is the other half: the small amount of arithmetic the template reads.
 *
 * ## A config, not an image
 *
 * Nine words and five colours. That is bytes rather than an upload, so there is
 * no presigned URL, no processing job, and nothing to orphan in a bucket - and
 * because it is structured, a character can be re-opened and edited rather than
 * replaced. It also cannot be a slur or a photograph of somebody, which an
 * uploaded avatar can.
 */

export interface Character {
  hair: string;
  face: string;
  expr: string;
  outfit: string;
  legs: string;
  acc: string;
  item: string;
  wrist: boolean;
  chain: boolean;
  ear: boolean;
  hairColor: string;
  eyeColor: string;
  outfitColor: string;
  skinColor: string;
  shoeColor: string;
}

/** What a character starts as, before anybody chooses anything. */
export const DEFAULT: Character = {
  hair: 'swept',
  face: 'none',
  expr: 'neutral',
  outfit: 'jacket',
  legs: 'pants',
  acc: 'band',
  item: 'none',
  wrist: false,
  chain: false,
  ear: false,
  hairColor: '#3a3a45',
  eyeColor: '#3f6bd8',
  outfitColor: '#8f95a3',
  skinColor: '#f6d3ae',
  shoeColor: '#4a2f22',
};

/** Every choice, with the words a person reads rather than the keys stored. */
export const OPTIONS = {
  expr: [
    ['neutral', 'Neutral'], ['happy', 'Happy'], ['laughing', 'Laughing'], ['sad', 'Sad'],
    ['angry', 'Angry'], ['surprised', 'Surprised'], ['wink', 'Wink'], ['smug', 'Smug'],
    ['love', 'Hearts'], ['dizzy', 'Dizzy'],
  ],
  hair: [
    ['swept', 'Side-swept'], ['spiky', 'Spiky'], ['quiff', 'Quiff'], ['slick', 'Slick back'],
    ['under', 'Undercut'], ['buzz', 'Buzz fade'], ['flat', 'Flat top'], ['messy', 'Bedhead'],
    ['curtain', 'Curtains'], ['mullet', 'Mullet'], ['mohawk', 'Mohawk'], ['rows', 'Cornrows'],
    ['dreads', 'Locs'], ['curls', 'Curls'], ['long', 'Long'], ['bun', 'Top bun'],
    ['tail', 'Ponytail'], ['bald', 'Receding'], ['shaved', 'Shaved'],
  ],
  face: [
    ['none', 'Clean'], ['stubble', 'Stubble'], ['moustache', 'Moustache'],
    ['handlebar', 'Handlebar'], ['goatee', 'Goatee'], ['chinstrap', 'Chin strap'],
    ['chops', 'Mutton chops'], ['beard', 'Full beard'],
  ],
  outfit: [
    ['jacket', 'Jacket + scarf'], ['hoodie', 'Hoodie'], ['tee', 'Tee'], ['tank', 'Tank'],
    ['blazer', 'Blazer + tie'], ['jersey', 'Jersey'], ['flannel', 'Flannel'], ['varsity', 'Varsity'],
  ],
  legs: [['pants', 'Pants'], ['jogger', 'Joggers'], ['shorts', 'Shorts'], ['skirt', 'Skirt']],
  acc: [
    ['none', 'None'], ['band', 'Headband'], ['cap', 'Cap'], ['beanie', 'Beanie'],
    ['glasses', 'Glasses'], ['shades', 'Shades'], ['phones', 'Headphones'],
  ],
  item: [['none', 'Empty'], ['glow', 'Glowstick'], ['phone', 'Phone'], ['cup', 'Drink']],
} as const;

/** The colours on offer, per part. */
export const PALETTE = {
  hairColor: ['#3a3a45', '#1c1a20', '#6b4a2f', '#a8703c', '#c9a227', '#b8452f', '#7a5cc4', '#3f7f8f', '#e8e4dc'],
  eyeColor: ['#3f6bd8', '#2f9fb8', '#4a8f5c', '#8fae4a', '#a8763c', '#7a4a2f', '#5a4a8f', '#b8452f', '#2b2b33'],
  outfitColor: ['#8f95a3', '#3f4a63', '#a34b5e', '#4a6b53', '#2b2b33', '#c9743f', '#6b5aa8', '#d8cfc0'],
  skinColor: ['#f8dcc0', '#f6d3ae', '#eab88a', '#d09a63', '#c98a5e', '#8d5a3b', '#5c3a28'],
  shoeColor: ['#4a2f22', '#241f26', '#e8e4dc', '#b8452f', '#3f4a63'],
} satisfies Record<string, string[]>;

/** Brow, eye and mouth for each expression. */
const EXPR: Record<string, { brow: string; eye: string; mouth: string }> = {
  neutral: { brow: 'flat', eye: 'open', mouth: 'neutral' },
  happy: { brow: 'raised', eye: 'happy', mouth: 'smile' },
  laughing: { brow: 'raised', eye: 'happy', mouth: 'laugh' },
  sad: { brow: 'sad', eye: 'open', mouth: 'frown' },
  angry: { brow: 'angry', eye: 'open', mouth: 'grit' },
  surprised: { brow: 'raised', eye: 'open', mouth: 'oh' },
  wink: { brow: 'raised', eye: 'wink', mouth: 'smirk' },
  smug: { brow: 'flat', eye: 'lid', mouth: 'smirk' },
  love: { brow: 'raised', eye: 'heart', mouth: 'smile' },
  dizzy: { brow: 'flat', eye: 'x', mouth: 'oh' },
};

/** Lighten or darken a hex colour by a flat amount per channel. */
function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);

  return (
    '#' +
    [n >> 16, (n >> 8) & 255, n & 255]
      .map((channel) =>
        Math.max(0, Math.min(255, Math.round(channel + amount)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/**
 * Anything unrecognised falls back to the default rather than drawing nothing.
 *
 * A stored character is a row of words that will outlive the option list it was
 * written from - somebody's hairstyle can be retired, and their face should not
 * disappear when it is. Same argument as the profile's widget layout.
 */
export function normalise(stored: unknown): Character {
  const raw = (stored ?? {}) as Partial<Character>;
  const pick = (key: keyof typeof OPTIONS, value: unknown): string => {
    const known = (OPTIONS[key] as readonly (readonly [string, string])[]).some(
      ([option]) => option === value,
    );

    return known ? (value as string) : (DEFAULT[key] as string);
  };

  const colour = (key: string, value: unknown): string =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
      ? value
      : (DEFAULT[key as keyof Character] as string);

  return {
    hair: pick('hair', raw.hair),
    face: pick('face', raw.face),
    expr: pick('expr', raw.expr),
    outfit: pick('outfit', raw.outfit),
    legs: pick('legs', raw.legs),
    acc: pick('acc', raw.acc),
    item: pick('item', raw.item),
    wrist: raw.wrist === true,
    chain: raw.chain === true,
    ear: raw.ear === true,
    hairColor: colour('hairColor', raw.hairColor),
    eyeColor: colour('eyeColor', raw.eyeColor),
    outfitColor: colour('outfitColor', raw.outfitColor),
    skinColor: colour('skinColor', raw.skinColor),
    shoeColor: colour('shoeColor', raw.shoeColor),
  };
}

/**
 * A character from a string, the same one every time.
 *
 * Seeded from a username so somebody who has never opened the maker still has a
 * face, and the same face on every screen and every device. A random default
 * would change on each render, which is worse than a letter in a circle.
 */
export function fromSeed(seed: string): Character {
  let hash = 2166136261;

  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  // A fresh value per part, so two people who differ in one character do not
  // end up differing in one feature.
  const next = (): number => {
    hash = Math.imul(hash ^ (hash >>> 15), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);

    return ((hash ^= hash >>> 16) >>> 0) / 4294967296;
  };

  const one = <T,>(list: readonly T[]): T => list[Math.floor(next() * list.length)] as T;

  return {
    hair: one(OPTIONS.hair)[0],
    face: one(OPTIONS.face)[0],
    // Seeded characters are pleased to be there. Assigning somebody a face that
    // looks angry or dizzy without them choosing it is a worse first
    // impression than any hairstyle.
    expr: one([['neutral'], ['happy'], ['smug'], ['wink']] as const)[0],
    outfit: one(OPTIONS.outfit)[0],
    legs: one(OPTIONS.legs)[0],
    acc: one(OPTIONS.acc)[0],
    item: 'none',
    wrist: next() > 0.7,
    chain: next() > 0.8,
    ear: next() > 0.75,
    hairColor: one(PALETTE.hairColor),
    eyeColor: one(PALETTE.eyeColor),
    outfitColor: one(PALETTE.outfitColor),
    skinColor: one(PALETTE.skinColor),
    shoeColor: one(PALETTE.shoeColor),
  };
}

/** Every flag and colour the drawing reads. Ported from the generator. */
export function values(character: Character) {
  const c = normalise(character);
  // `??` is not enough for the checker here: an index into a Record is
  // possibly-undefined either side of it, and `neutral` is the one key this
  // file guarantees exists.
  const expression = EXPR[c.expr] ?? { brow: 'flat', eye: 'open', mouth: 'neutral' };
  const inner = shade(c.outfitColor, 58);

  return {
    skin: c.skinColor,
    hair: c.hairColor,
    eye: c.eyeColor,
    outfit: c.outfitColor,
    inner,
    accent: '#b8362f',
    shoe: c.shoeColor,
    hairLight: shade(c.hairColor, 48),
    hairDark: shade(c.hairColor, -26),
    pocket: shade(c.outfitColor, -28),
    legFill: shade(c.outfitColor, -46),
    skirtFill: shade(c.outfitColor, -18),

    sleeveLong: ['jacket', 'hoodie', 'blazer', 'flannel', 'varsity'].includes(c.outfit),
    sleeveShort: c.outfit === 'tee' || c.outfit === 'jersey',
    sleeveFill: c.outfit === 'varsity' ? inner : c.outfitColor,

    hSwept: c.hair === 'swept', hSpiky: c.hair === 'spiky', hQuiff: c.hair === 'quiff',
    hUnder: c.hair === 'under', hBuzz: c.hair === 'buzz', hMohawk: c.hair === 'mohawk',
    hCurls: c.hair === 'curls', hBun: c.hair === 'bun', hTail: c.hair === 'tail',
    hSlick: c.hair === 'slick', hCurtain: c.hair === 'curtain', hDreads: c.hair === 'dreads',
    hRows: c.hair === 'rows', hLong: c.hair === 'long', hMessy: c.hair === 'messy',
    hFlat: c.hair === 'flat', hMullet: c.hair === 'mullet', hBald: c.hair === 'bald',

    fStubble: c.face === 'stubble', fMoustache: c.face === 'moustache',
    fGoatee: c.face === 'goatee', fBeard: c.face === 'beard',
    fChinstrap: c.face === 'chinstrap', fChops: c.face === 'chops',
    fHandlebar: c.face === 'handlebar',

    bFlat: expression.brow === 'flat', bRaised: expression.brow === 'raised',
    bAngry: expression.brow === 'angry', bSad: expression.brow === 'sad',
    eOpen: expression.eye === 'open', eLid: expression.eye === 'lid',
    eHappy: expression.eye === 'happy', eWink: expression.eye === 'wink',
    eHeart: expression.eye === 'heart', eX: expression.eye === 'x',
    mNeutral: expression.mouth === 'neutral', mSmile: expression.mouth === 'smile',
    mLaugh: expression.mouth === 'laugh', mFrown: expression.mouth === 'frown',
    mGrit: expression.mouth === 'grit', mOh: expression.mouth === 'oh',
    mSmirk: expression.mouth === 'smirk',

    oJacket: c.outfit === 'jacket', oHoodie: c.outfit === 'hoodie', oTee: c.outfit === 'tee',
    oTank: c.outfit === 'tank', oBlazer: c.outfit === 'blazer', oJersey: c.outfit === 'jersey',
    oFlannel: c.outfit === 'flannel', oVarsity: c.outfit === 'varsity',

    lShorts: c.legs === 'shorts', lSkirt: c.legs === 'skirt', lJogger: c.legs === 'jogger',

    aBand: c.acc === 'band', aCap: c.acc === 'cap', aBeanie: c.acc === 'beanie',
    aGlasses: c.acc === 'glasses', aShades: c.acc === 'shades', aPhones: c.acc === 'phones',

    iGlow: c.item === 'glow', iPhone: c.item === 'phone', iCup: c.item === 'cup',
    jWrist: c.wrist, jChain: c.chain, jEar: c.ear,

    // The generator animates; this does not, yet. The class is what the
    // drawing expects, and an idle character is the right thing in a crowd
    // that may have thirty of them on screen at once.
    animClass: 'a-idle',
  };
}

export type CharacterValues = ReturnType<typeof values>;
